import { subDays } from "date-fns";
import moment from "moment";
import { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { TableColumn } from "react-data-table-component";
import { useSelector } from "react-redux";
import AlertModal from "../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import CustomDatePicker from "../../../components/customDatePicker/CustomDatePicker";
import DataTableBase from "../../../components/dataTableBase/DataTableBase";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import ProductAndCircuitIdSelector from "../../../components/productAndCircuitIdSelector/ProductAndCircuitIdSelector";
import { ISelectOption } from "../../../components/selectOption/SelectOption";
import Title from "../../../components/title/Title";
import { EPackageType } from "../../../interfaces/packageType";
import { PROFILE_ACTIONS } from "../../../interfaces/profileData";
import { IPurchaseHistory } from "../../../interfaces/purchaseHistory";
import { getPurchaseHistory } from "../../../services/dashboard";
import { IRootState } from "../../../store/configureStore";
import { getDataWithCheckingActionPrivilege } from "../../../util/hasActionPrivileges";

const PurchaseHistory = () => {
  const { circuitByUserIdLoading } = useSelector(
    ({ dashboard }: IRootState) => ({
      circuitByUserIdLoading: dashboard.loading,
    })
  );
  const [selectedPackageType, setSelectedPackageType] = useState<
    EPackageType | []
  >([]);
  const [selectedCircuitId, setSelectedCircuitId] = useState<
    ISelectOption | []
  >([]);

  const [purchaseHistory, setPurchaseHistory] = useState<IPurchaseHistory[]>(
    []
  );
  const [loadingPurchaseHistory, setLoadingPurchaseHistory] =
    useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [broadbandId, setBroadbandId] = useState<string>();

  const [fromDate, setFromDate] = useState<Date>(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 1,
      new Date().getDate()
    )
  );
  const [toDate, setToDate] = useState<Date>(new Date());

  const handleChangeCircuitId = (value: ISelectOption | []) => {
    setSelectedCircuitId(value);
  };

  const submitDataHandler = () => {
    const broadbandOrCircuitId = getEnteredCircuitOrBroadbandId();
    if (!broadbandOrCircuitId) {
      return;
    }
    setLoadingPurchaseHistory(true);
    setPurchaseHistory([]);

    getDataWithCheckingActionPrivilege(
      selectedPackageType === EPackageType.BROADBAND,
      broadbandOrCircuitId,
      PROFILE_ACTIONS.PURCHASE_HISTORY,
      () =>
        getPurchaseHistory(
          broadbandOrCircuitId,
          moment(fromDate).format("yyyy-MM-DD"),
          moment(toDate).format("yyyy-MM-DD")
        )
    )
      .then((result: IPurchaseHistory[]) => {
        setLoadingPurchaseHistory(false);
        if (result.length <= 0) {
          setError(
            "There is no purchase history data according to selected search criteria."
          );
          return;
        }
        setPurchaseHistory(result.reverse());
      })
      .catch((error) => {
        setError(error.message);
        setLoadingPurchaseHistory(false);
      });
  };

  const columns: TableColumn<IPurchaseHistory>[] = [
    {
      name: "Subscriber ID",
      selector: (row: IPurchaseHistory) => row.subscriberId,
    },
    {
      name: "VAS Type",
      selector: (row: IPurchaseHistory) => row.vasType,
    },
    {
      name: "VAS Package",
      selector: (row: IPurchaseHistory) => row.vasPackage,
    },
    {
      name: "Transaction Time",
      sortable: true,
      width: "150px",
      selector: (row: IPurchaseHistory) => row.transactionTime,
    },
    {
      name: "Valid Till",
      width: "150px",
      selector: (row: IPurchaseHistory) => row.validTill,
    },
    {
      name: "Duration",
      selector: (row: IPurchaseHistory) => row.duration,
    },
    {
      name: "Channel",
      selector: (row: IPurchaseHistory) => row.channel,
    },
  ];

  const getEnteredCircuitOrBroadbandId = (): string => {
    return selectedPackageType === EPackageType.BROADBAND
      ? broadbandId
      : (selectedCircuitId as ISelectOption).value;
  };

  return (
    <div className="page-container">
      <MainLoader show={circuitByUserIdLoading || loadingPurchaseHistory} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/serviceLineUp", title: "Service Line-up" },
          { path: "", title: "Purchase History" },
        ]}
      />

      <Title>Purchase History</Title>

      <ProductAndCircuitIdSelector
        className="mt-1"
        selectedCircuitId={selectedCircuitId}
        selectedPackageType={selectedPackageType}
        setSelectedPackageType={setSelectedPackageType}
        handleChangeCircuitId={handleChangeCircuitId}
        onChangeBroadbandId={(id) => setBroadbandId(id)}
        shouldShowSearchIcon={false}
      />

      <Row className="my-2 row-cols-1 row-cols-md-2 gy-2">
        <Col>
          <CustomDatePicker
            startDate={fromDate}
            onChange={(value) => value && setFromDate(value)}
            label="From"
            textAlign="left"
            maxDate={new Date()}
            selected={fromDate}
            placeholderText="yyyy-mm-dd"
            dateFormat="yyyy-MM-dd"
            selectsStart
            minDate={subDays(new Date(), 90)}
          />
        </Col>
        <Col>
          <CustomDatePicker
            onChange={(value) => value && setToDate(value)}
            textAlign="left"
            label="To"
            maxDate={new Date()}
            selected={toDate}
            placeholderText="yyyy-mm-dd"
            dateFormat="yyyy-MM-dd"
            selectsEnd
            minDate={fromDate}
          />
        </Col>
      </Row>

      <Row className="mb-5 mt-3 justify-content-center">
        <Col className="col-md-3">
          <Button
            className="w-100"
            variant="success"
            onClick={submitDataHandler}
            disabled={!getEnteredCircuitOrBroadbandId() || !selectedPackageType}
          >
            Submit
          </Button>
        </Col>
      </Row>

      <DataTableBase
        className="mt-3"
        columns={columns}
        data={purchaseHistory}
        progressPending={loadingPurchaseHistory}
        noDataComponent={<></>}
        progressComponent={<></>}
      />

      {purchaseHistory.length <= 0 && (
        <Row className="justify-content-center">
          <Col className="col-4 d-none d-md-block">
            <EmptyPlaceholder />
          </Col>
        </Row>
      )}

      <AlertModal
        show={!!error}
        variant="error"
        buttons={[
          {
            text: "OK",
            variant: "success",
            onClick: () => setError(""),
          },
        ]}
      >
        {error}
      </AlertModal>
    </div>
  );
};

export default PurchaseHistory;
